import { AppointmentsContainer } from "@/lib/appointments/container"
import { AppointmentsContext } from "@/lib/appointments/context"
import type { Authentication } from "@/lib/authentication"
import type { BookingSearch } from "@/lib/bookingSearch"
import type { Businesses } from "@/lib/businesses"
import type { Caching } from "@/lib/caching"
import type { Mirror } from "@/lib/mirror"
import { type Model, Modelling } from "@/lib/models"
import type { Practitioners } from "@/lib/practitioners"
import type { Services } from "@/lib/services"

export namespace Appointments {
  export type ContextData = {
    perform: Functions.Perform
    processing: string | undefined
    results: PerformResults
  }

  export const Container = AppointmentsContainer
  export const Context = AppointmentsContext

  export type Offering = {
    id: AppointmentType[`id`]
    appointments: Mirror<AppointmentResult>
    appointmentType: AppointmentType
  }

  export const OfferingModel: Model<`AppointmentOffering`> = () => {
    return {
      id: `string`,
      appointments: Modelling.reference(`mirror`, `AppointmentResult`),
      appointmentType: `AppointmentType`,
    }
  }

  export type BookableAppointment = {
    address_1: Businesses.Business[`attributes`][`address_1`]
    address_2: Businesses.Business[`attributes`][`address_2`]
    appointmentId: AppointmentResult[`id`]
    appointmentType: AppointmentType[`attributes`][`name`]
    appointmentTypeId: AppointmentType[`id`]
    city: Businesses.Business[`attributes`][`city`]
    post_code: Businesses.Business[`attributes`][`post_code`]
    practice: Businesses.Business[`attributes`][`name`]
    practitioner: Practitioners.Practitioner[`attributes`][`name`]
    practitionerId: Practitioners.Practitioner[`id`]
    price_cents: AppointmentType[`attributes`][`price_cents`]
    region: Businesses.Business[`attributes`][`state`]
    specialty: Services.Option
    timeframe: BookingSearch.Timeframe
    timeslot: string
    userEmail: Authentication.Patient[`email`]
    userFirstName: Authentication.Patient[`firstName`]
    userLastName: Authentication.Patient[`lastName`]
    userUID: Authentication.User[`uid`]
  }

  export type AppointmentDetails = {
    address_1: string
    address_2: string
    appointmentId?: number
    appointmentIds: number[]
    appointmentTypeId: string
    appointmentType: string
    city: string
    post_code: string
    practice: string
    practitionerId: string
    practitioner: string
    price_cents: number
    region: string
    specialty: string
    timeframe: string
    timeslot: string
    userEmail: string
    userFirstName: string
    userLastName: string
    userUID: string
  }

  export type AppointmentResult = {
    id: string
    type: `available_appointment`
    attributes: {
      appointment_type: { data: AppointmentType }
      commission_percentage: number
      practitioner: { data: Practitioners.Practitioner }
      start_time: string
    }
  }

  export const AppointmentResultModel: Model<`AppointmentResult`> = () => {
    return {
      id: `string`,
      type: Modelling.equality(`item`, `available_appointment`),
      attributes: {
        appointment_type: { data: `AppointmentType` },
        commission_percentage: `number`,
        practitioner: { data: `Practitioner` },
        start_time: `string`,
      },
    }
  }

  export type AppointmentType = {
    id: string
    type: `appointment_type`
    attributes: {
      active: boolean
      description: string
      duration_in_minutes: number
      name: string
      price_cents: number
    }
  }

  export const AppointmentTypeModel: Model<`AppointmentType`> = () => {
    return {
      id: `string`,
      type: Modelling.equality(`item`, `appointment_type`),
      attributes: {
        active: `boolean`,
        description: `string`,
        duration_in_minutes: `number`,
        name: `string`,
        price_cents: `number`,
      },
    }
  }

  export type Key = Caching.Key

  export type PracticeId = Key
  export type StartTime = Key

  export type PerformResults = {
    appointmentOfferings: Caching.Payload<OfferingsCache.Data>
  }

  export namespace OfferingsCache {
    export const expiry = 1000 * 60 * 5 // 5 minutes

    export type Data = Mirror<Offering>

    export type KeyData = {
      practiceId: PracticeId
      service: Services.Option
      startTime: StartTime
    }

    export type Keys = [`appointments`, Services.Option, PracticeId, StartTime]

    export type Payload = Caching.Payload<Data>

    export type Store = Record<
      Services.Option,
      Record<PracticeId, Record<StartTime, Payload>>
    >

    export const validator = () => {
      return Modelling.validator(`AppointmentType`)
    }
  }

  export namespace Functions {
    export type Perform = () => Promise<PerformResults>
  }
}
